import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type {
  KidsData,
  KidsMagicStarsData,
  KidsEmojiYearData,
  KidsDreamPickerData,
  KidsSuperPowerData,
  KidsAIContent,
} from '../types/kids'

interface KidsStore {
  kidsData: KidsData
  isGenerating: boolean

  // Actions
  setName: (name: string) => void
  setMagicStars: (data: KidsMagicStarsData) => void
  setEmojiYear: (data: KidsEmojiYearData) => void
  setDreamPicker: (data: KidsDreamPickerData) => void
  setSuperPower: (data: KidsSuperPowerData) => void
  setAIContent: (content: KidsAIContent) => void
  generateAIContent: () => Promise<void>
  markComplete: () => void
  reset: () => void

  // Helpers
  getCompletedExercises: () => string[]
  isAllComplete: () => boolean
}

const initialKidsData: KidsData = {
  name: '',
  magicStars: null,
  emojiYear: null,
  dreamPicker: null,
  superPower: null,
  aiContent: null,
  completedAt: null,
}

export const useKidsStore = create<KidsStore>()(
  persist(
    (set, get) => ({
      kidsData: initialKidsData,
      isGenerating: false,

      setName: (name) =>
        set((state) => ({
          kidsData: { ...state.kidsData, name },
        })),

      setMagicStars: (data) =>
        set((state) => ({
          kidsData: { ...state.kidsData, magicStars: data },
        })),

      setEmojiYear: (data) =>
        set((state) => ({
          kidsData: { ...state.kidsData, emojiYear: data },
        })),

      setDreamPicker: (data) =>
        set((state) => ({
          kidsData: { ...state.kidsData, dreamPicker: data },
        })),

      setSuperPower: (data) =>
        set((state) => ({
          kidsData: { ...state.kidsData, superPower: data },
        })),

      setAIContent: (content) =>
        set((state) => ({
          kidsData: { ...state.kidsData, aiContent: content },
        })),

      generateAIContent: async () => {
        const { kidsData, isGenerating } = get()
        if (isGenerating || kidsData.aiContent) return // Already generating or have content

        set({ isGenerating: true })

        try {
          const response = await fetch('/api/kids-wrapped', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              name: kidsData.name,
              magicStars: kidsData.magicStars,
              emojiYear: kidsData.emojiYear,
              dreamPicker: kidsData.dreamPicker,
              superPower: kidsData.superPower,
            }),
          })

          if (response.ok) {
            const data = await response.json()
            set((state) => ({
              kidsData: { ...state.kidsData, aiContent: data },
              isGenerating: false,
            }))
          } else {
            set({ isGenerating: false })
          }
        } catch (error) {
          console.error('Failed to generate AI content:', error)
          set({ isGenerating: false })
        }
      },

      markComplete: () =>
        set((state) => ({
          kidsData: {
            ...state.kidsData,
            completedAt: new Date().toISOString(),
          },
        })),

      reset: () => set({ kidsData: initialKidsData, isGenerating: false }),

      getCompletedExercises: () => {
        const { kidsData } = get()
        const completed: string[] = []
        if (kidsData.magicStars) completed.push('magic-stars')
        if (kidsData.emojiYear) completed.push('emoji-year')
        if (kidsData.dreamPicker) completed.push('dream-picker')
        if (kidsData.superPower) completed.push('super-power')
        return completed
      },

      isAllComplete: () => {
        const { kidsData } = get()
        return !!(
          kidsData.magicStars &&
          kidsData.emojiYear &&
          kidsData.dreamPicker &&
          kidsData.superPower
        )
      },
    }),
    {
      name: 'spark-kids-storage',
    }
  )
)
